import '.ev{border:1px solid transparent;}.ssr-plugin'
import '.f3{outline:none;}.ssr-plugin'
import '.dw{text-align:right;}.ssr-plugin'
import '.an{background:transparent;}.ssr-plugin'
import '.he{height:36px;}.ssr-plugin'
import '.fm{padding-right:8px;}.ssr-plugin'
import '.jj{max-width:112px;}.ssr-plugin'
import React, { useState, useEffect } from 'react'
import { styled, type Style } from 'inlines'
import { isValid, parse, addHours, addMinutes } from 'date-fns'
import { radius } from '../../utils/radius.js'
import { colors } from '../../tokens/colors.js'
import { TEXT_VARIANT_STYLES } from '../../tokens/typography.js'

const formatInput = (value: string): string => {
  // Remove any non-digit characters
  const digits = value.replace(/\D/g, '')

  // Add slashes as the user types
  if (digits.length <= 2) return digits
  if (digits.length <= 4) return `${digits.slice(0, 2)}/${digits.slice(2)}`
  return `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4, 8)}`
}

const parseMultipleFormats = (dateString: string): Date | null => {
  const cleanInput = dateString.trim()
  if (!cleanInput) return null

  // Split by common separators
  const parts = cleanInput
    .split(/[\/\-\.]/)
    .map((part) => part.trim())
    .filter((part) => part)
  if (parts.length !== 3) return null

  const [part1, part2, part3] = parts.map(Number)

  // Invalid if any part is NaN or 0
  if (!part1 || !part2 || !part3) return null

  // Convert 2-digit years to 4-digit (assuming 20xx for 00-30, 19xx for 31-99)
  const expandYear = (year: number): number => {
    if (year >= 1900 && year <= 2100) return year
    if (year <= 30) return 2000 + year
    if (year <= 99) return 1900 + year
    return year
  }

  // Try formats in hierarchy order (top overrides bottom)
  const formats = [
    // mm/dd/yyyy (current) - 4-digit year
    {
      month: part1,
      day: part2,
      year: expandYear(part3),
      condition: part3 >= 1900 || part3 <= 99,
    },
    // mm/dd/yy - 2-digit year
    {
      month: part1,
      day: part2,
      year: expandYear(part3),
      condition: part3 <= 99,
    },
    // m/d/yyyy - single digit month/day, 4-digit year
    {
      month: part1,
      day: part2,
      year: expandYear(part3),
      condition: part3 >= 1900,
    },
    // m/d/yy - single digit month/day, 2-digit year
    {
      month: part1,
      day: part2,
      year: expandYear(part3),
      condition: part3 <= 99,
    },
    // dd/mm/yyyy - day/month, 4-digit year
    {
      month: part2,
      day: part1,
      year: expandYear(part3),
      condition: part3 >= 1900,
    },
    // dd/mm/yy - day/month, 2-digit year
    {
      month: part2,
      day: part1,
      year: expandYear(part3),
      condition: part3 <= 99,
    },
    // d/m/yyyy - single digit day/month, 4-digit year
    {
      month: part2,
      day: part1,
      year: expandYear(part3),
      condition: part3 >= 1900,
    },
    // d/m/yy - single digit day/month, 2-digit year
    {
      month: part2,
      day: part1,
      year: expandYear(part3),
      condition: part3 <= 99,
    },
    // yyyy/mm/dd - ISO-like format, 4-digit year first
    {
      month: part2,
      day: part3,
      year: expandYear(part1),
      condition: part1 >= 1900,
    },
    // yyyy/m/d - ISO-like format with single digits
    {
      month: part2,
      day: part3,
      year: expandYear(part1),
      condition: part1 >= 1900,
    },
    // yy/mm/dd - 2-digit year first
    {
      month: part2,
      day: part3,
      year: expandYear(part1),
      condition: part1 <= 99,
    },
    // yy/m/d - 2-digit year first with single digits
    {
      month: part2,
      day: part3,
      year: expandYear(part1),
      condition: part1 <= 99,
    },
  ]

  // Try each format in order
  for (const format of formats) {
    if (!format.condition) continue

    const { month, day, year } = format

    // Basic validation
    if (month < 1 || month > 12) continue
    if (day < 1 || day > 31) continue
    if (year < 1900 || year > 2100) continue

    // Try to create the date
    try {
      const dateStr = `${month.toString().padStart(2, '0')}/${day.toString().padStart(2, '0')}/${year}`
      const date = parse(dateStr, 'MM/dd/yyyy', new Date())

      if (isValid(date)) {
        return date
      }
    } catch (e) {
      continue
    }
  }

  return null
}

const isValidDate = (dateString: string): boolean => {
  return parseMultipleFormats(dateString) !== null
}

const extractValidDateParts = (input: string): string => {
  // Try to extract numbers that could form a date
  const numbers = input.match(/\d+/g) || []

  if (numbers.length >= 3) {
    // Join with slashes and try to parse
    const candidateDate = numbers.slice(0, 3).join('/')
    const parsedDate = parseMultipleFormats(candidateDate)

    if (parsedDate) {
      // Return in mm/dd/yyyy format
      const month = String(parsedDate.getMonth() + 1).padStart(2, '0')
      const day = String(parsedDate.getDate()).padStart(2, '0')
      const year = parsedDate.getFullYear()
      return `${month}/${day}/${year}`
    }
  }

  return ''
}

export const DateTextInput = ({
  format = 'hh:mm',
  value: rawValue,
  onBlur,
  onChange,
  autoFocus,
  ...props
}: {
  format?: string
  value?: Date
  autoFocus?: boolean
  onBlur?: React.FocusEventHandler<HTMLInputElement>
  onChange: (e: any) => any
}) => {
  let hours = rawValue?.getHours() ?? 0
  let minutes = rawValue?.getMinutes() ?? 0

  const [subState, setSubState] = useState<string>('')
  const [isTyping, setIsTyping] = useState(false)

  useEffect(() => {
    if (!isTyping) {
      if (rawValue) {
        const month = String(rawValue.getMonth() + 1).padStart(2, '0')
        const day = String(rawValue.getDate()).padStart(2, '0')
        const year = rawValue.getFullYear()
        setSubState(`${month}/${day}/${year}`)
      } else {
        setSubState('')
      }
    }
  }, [rawValue, isTyping])

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    setIsTyping(false)

    const currentValue = subState

    // First try to parse the input directly
    let parsedDate = parseMultipleFormats(currentValue)

    if (!parsedDate) {
      // If direct parsing fails, try to extract valid parts
      const cleanedValue = extractValidDateParts(currentValue)
      if (cleanedValue) {
        parsedDate = parseMultipleFormats(cleanedValue)
      }
    }

    if (parsedDate) {
      // Valid date found - format as mm/dd/yyyy for display
      const month = String(parsedDate.getMonth() + 1).padStart(2, '0')
      const day = String(parsedDate.getDate()).padStart(2, '0')
      const year = parsedDate.getFullYear()
      const formattedDate = `${month}/${day}/${year}`

      setSubState(formattedDate)

      // Add hours and minutes back
      let newDate = addHours(parsedDate, hours)
      newDate = addMinutes(newDate, minutes)
      onChange(newDate)
    } else {
      // No valid date found - clear it
      setSubState('')
      onChange(null)
    }

    // Call original onBlur if provided
    onBlur?.(e)
  }

  const handleFocus = () => {
    setIsTyping(true)
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value
    setSubState(inputValue)

    // During typing, try to parse if it looks like a complete date
    const parsedDate = parseMultipleFormats(inputValue)
    if (parsedDate) {
      let newDate = addHours(parsedDate, hours)
      newDate = addMinutes(newDate, minutes)
      onChange(newDate)
    }
  }

  // Use Text input with Ghost variant instead of a styled.input below

  return (
    <styled.input className="ev f3 dw an he fm jj"
      type="text"
      placeholder="Add date"
      value={subState}
      maxLength={12}
      autoFocus={autoFocus}
      onFocus={handleFocus}
      onBlur={handleBlur}
      onChange={handleChange}
      style={{
        borderRadius: radius[12],
        color: colors.neutral80,
        ...TEXT_VARIANT_STYLES['display-regular'],
      }}
      {...props}
    />
  )
}
